import React, { useEffect, useState, useCallback, useMemo } from "react";
import { HoofdcodesState, hoofdcodesActions } from "@/store/reducers/hoofdcodes";
import { hoofdcodeFilterActions, HoofdcodeFilterState } from "@/store/reducers/hoofdcode_filter";
import { connect, useDispatch } from "react-redux";
import { RootState } from "@/store";
import apiService from "@/api-service";
import { Hoofdcode } from "@/models/hoofdcode";
import { Werkcat } from "@/models/werkcat";
import { Spinner, Row, Col, Table, Container, Form, Button, Dropdown, InputGroup } from "react-bootstrap";

import { WerkcatsState } from "@/store/reducers/werkcats";
import { SubwerkcatsState } from "@/store/reducers/subwerkcats";
import { Subwerkcat } from "@/models/subwerkcat";
import { PaginationControl } from "react-bootstrap-pagination-control";
import { useNavigate } from "react-router-dom";
import HoofdcodeWerkCategorieFilter from '@/components/hoofdcode_filters/werkcategorie_filter';
import HoofdcodeSubwerkCategorieFilter from '@/components/hoofdcode_filters/subwerkcategorie_filter';
import HoofdcodeCSVExportModal from "@/components/hoofdcode-csv-export-modal/hoofdcode-csv-export-modal";
import HoofdCodeFilter from "@/components/hoofdcode_filters/hoofdcode_filter";
import { MdDownload, MdArrowUpward, MdArrowDownward } from "react-icons/md";
import FilterInfoButton from "@/components/common/filter-info-button";
import useLoading from "@/hooks/loading.hook";
import useAPIDownload from "@/hooks/download.hook";
import { useDebounce } from "@/hooks/debounce.hook";

interface Props {
    hoofdcodes_state: HoofdcodesState;
    werkcat_state: WerkcatsState;
    subwerkcat_state: SubwerkcatsState;
    hoofdcode_filter: HoofdcodeFilterState;
}

function mapStateToProps(state: RootState): Props {
    return {
        hoofdcodes_state: state.hoofdcodes,
        werkcat_state: state.werkcats,
        subwerkcat_state: state.subwerkcats,
        hoofdcode_filter: state.hoofdcode_filter
    }
}

interface SortConfig {
    key: keyof Hoofdcode | 'vermeldingen' | 'bestekken';
    direction: 'asc' | 'desc';
}

interface SortableHeaderProps {
    children: React.ReactNode;
    sortKey: keyof Hoofdcode | 'vermeldingen' | 'bestekken';
    currentSort: SortConfig | null;
    onSort: (key: keyof Hoofdcode | 'vermeldingen' | 'bestekken') => void;
    style?: React.CSSProperties;
}

const SortableHeader: React.FC<SortableHeaderProps> = ({ children, sortKey, currentSort, onSort, style }) => {
    const isActive = currentSort?.key === sortKey;
    const direction = isActive ? currentSort.direction : null;

    return (
        <th 
            style={{ 
                ...style, 
                cursor: 'pointer', 
                userSelect: 'none',
                position: 'relative'
            }}
            onClick={() => onSort(sortKey)}
            className={isActive ? 'table-active' : ''}
        >
            <div className="d-flex align-items-center justify-content-between">
                <span>{children}</span>
                <span style={{ marginLeft: '5px', opacity: isActive ? 1 : 0.3 }}>
                    {direction === 'asc' ? <MdArrowUpward size={14} /> : <MdArrowDownward size={14} />}
                </span>
            </div>
        </th>
    );
};

function HoofdcodesList(props: Props) {
    const navigate = useNavigate();
    const dispatch = useDispatch();
    const loader = useLoading();
    const downloadFromAPI = useAPIDownload();

    const beschrijving = props.hoofdcode_filter.beschrijving;
    const categories = props.hoofdcode_filter.werk_categorieen;
    const subcategories = props.hoofdcode_filter.subwerk_categorieen;
    const selectedHoofdcodes = props.hoofdcode_filter.hoofdcodes;
    const jaarversie = props.hoofdcode_filter.jaarversie;
    const release = props.hoofdcode_filter.release;
    const sortConfig = props.hoofdcode_filter.sortConfig;
    const activePage = props.hoofdcode_filter.activePage;
    const filterVisible = props.hoofdcode_filter.filterVisible;

    const [totalCount, setTotalCount] = useState<number>(-1);
    const [beschikbareJaarversies, setBeschikbareJaarversies] = useState<string[]>([]);
    // NIEUW: "release versie" (Bestek.versie) — cascadeert onder jaarversie, zelfde bron als
    // de bestekken-filter (/bestek/releases).
    const [beschikbareReleases, setBeschikbareReleases] = useState<string[]>([]);
    const [hoofdcodes, setHoofdcodes] = useState<Hoofdcode[]>();
    const [loading, setLoading] = useState<boolean>(true);
    const [csvExportModalShown, setCsvExportModalShown] = useState<boolean>(false);
    const [pageInput, setPageInput] = useState<string>("");

    const pageSize = 10;

    const debouncedBeschrijving = useDebounce(beschrijving, 500);
    const categoryIds = useMemo(() => categories.map(c => c.id), [categories]);
    const subcategoryIds = useMemo(() => subcategories.map(c => c.id), [subcategories]);

    const handleSort = useCallback((key: keyof Hoofdcode | 'vermeldingen' | 'bestekken') => {
        let direction: 'asc' | 'desc' = 'asc';
        
        if (sortConfig && sortConfig.key === key && sortConfig.direction === 'asc') {
            direction = 'desc';
        }
        
        dispatch(hoofdcodeFilterActions.set_sort_config({ key, direction }));
    }, [sortConfig, dispatch]);

    const fetch = useCallback(() => {
        setLoading(true);
        let params: { [key: string]: any } = {
            pagesize: pageSize,
            page: activePage,
            include_usage: true,
            description: debouncedBeschrijving
        }
    
        console.log('🚀 Frontend fetch called with:', {
            selectedHoofdcodes: selectedHoofdcodes.length,
            categoryIds: categoryIds.length,
            subcategoryIds: subcategoryIds.length
        });
    
        // CRITICAL FIX: Hoofdcode filter logica
        if (selectedHoofdcodes.length > 0) {
            const regularHoofdcodes = selectedHoofdcodes.filter(hc => hc.id > 0);
            const selected99Codes = selectedHoofdcodes.filter(hc => hc.id < 0);

            console.log('   Regular hoofdcodes:', regularHoofdcodes.length);
            console.log('   99 codes:', selected99Codes.length);

            // FIXED: altijd ALLE geselecteerde ID's meesturen (regulier én 99-codes), niet
            // alleen de reguliere. De backend gebruikt dit zowel om reguliere codes te
            // filteren als om te weten dat er iets specifieks geselecteerd is (zodat hij niet
            // stilzwijgend terugvalt op "toon alle reguliere codes" wanneer alleen 99-codes
            // zijn gekozen), én om precies de gekozen 99-code-jaarversie(s) uit de bredere
            // categorie-aggregatie te filteren.
            params['hoofdcode_ids'] = selectedHoofdcodes.map(hc => hc.id);
            console.log('   Sending hoofdcode_ids:', params['hoofdcode_ids']);

            if (regularHoofdcodes.length > 0) {
                // IMPORTANT: Still send category filters to narrow down results
                if (categoryIds.length > 0) {
                    params['werk_categorie_ids'] = categoryIds;
                    console.log('   Sending werk_categorie_ids:', params['werk_categorie_ids']);
                }
                if (subcategoryIds.length > 0) {
                    params['subwerk_categorie_ids'] = subcategoryIds;
                    console.log('   Sending subwerk_categorie_ids:', params['subwerk_categorie_ids']);
                }
            }

            if (selected99Codes.length > 0) {
                params['include_filtered_99_codes'] = true;
                
                const werkCodes = new Set<string>();
                const subwerkCodes = new Set<string>();
                
                selected99Codes.forEach(code => {
                    const parts = code.label.split('.');
                    if (parts.length === 3) {
                        if (parts[0] !== 'xx') werkCodes.add(parts[0]);
                        if (parts[1] !== 'xx') subwerkCodes.add(parts[1]);
                    }
                });
                
                if (werkCodes.size > 0) {
                    params['filter_99_werk_codes'] = Array.from(werkCodes);
                }
                if (subwerkCodes.size > 0) {
                    params['filter_99_subwerk_codes'] = Array.from(subwerkCodes);
                }
                
                console.log('   99 code filtering:', { werkCodes: Array.from(werkCodes), subwerkCodes: Array.from(subwerkCodes) });
            }
        } else {
            // NO hoofdcodes selected: apply category filters
            if (categoryIds.length > 0) {
                params['werk_categorie_ids'] = categoryIds;
                console.log('   No hoofdcodes - sending werk_categorie_ids:', params['werk_categorie_ids']);
            }
            if (subcategoryIds.length > 0) {
                params['subwerk_categorie_ids'] = subcategoryIds;
                console.log('   No hoofdcodes - sending subwerk_categorie_ids:', params['subwerk_categorie_ids']);
            }
            params['include_99_codes'] = true;
        }
    
        if (jaarversie) {
            params['versie_jaarversie'] = jaarversie;
        }

        if (release) {
            params['versie'] = release;
        }

        if (sortConfig) {
            params['sort_by'] = sortConfig.key;
            params['sort_direction'] = sortConfig.direction;
        }

        console.log('📤 Final API params:', params);

        apiService.post<{ result: Hoofdcode[], total_count: number }>("/hoofdcode/", params)
            .then((res) => {
                const totalPages = Math.ceil(res.data.total_count / pageSize);

                console.log('📥 API response:', { total: res.data.total_count, results: res.data.result.length });

                if (activePage > totalPages && totalPages > 0) {
                    dispatch(hoofdcodeFilterActions.set_active_page(totalPages));
                    return;
                }

                setHoofdcodes(res.data.result);
                setTotalCount(res.data.total_count);

            }, (error) => {
                console.error('API Error:', error);
                setHoofdcodes([]);
                setTotalCount(0);
            }).finally(() => setLoading(false));
    }, [debouncedBeschrijving, activePage, categoryIds, subcategoryIds, pageSize, sortConfig, selectedHoofdcodes, jaarversie, release, dispatch]);

    const getCurrentFilterParams = useCallback(() => {
        const params: any = {
            description: debouncedBeschrijving || undefined,
            include_usage: true
        };

        if (categoryIds.length > 0) {
            params.werk_categorie_ids = categoryIds;
        }
        if (subcategoryIds.length > 0) {
            params.subwerk_categorie_ids = subcategoryIds;
        }

        if (selectedHoofdcodes.length > 0) {
            const regularHoofdcodes = selectedHoofdcodes.filter(hc => hc.id > 0);
            const selected99Codes = selectedHoofdcodes.filter(hc => hc.id < 0);

            // FIXED: zelfde reden als in fetch() hierboven — altijd alle geselecteerde
            // ID's meesturen, niet alleen de reguliere.
            params.hoofdcode_ids = selectedHoofdcodes.map(hc => hc.id);

            if (selected99Codes.length > 0) {
                params.include_filtered_99_codes = true;
                
                const werkCodes = new Set<string>();
                const subwerkCodes = new Set<string>();
                
                selected99Codes.forEach(code => {
                    const parts = code.label.split('.');
                    if (parts.length === 3) {
                        werkCodes.add(parts[0]);
                        subwerkCodes.add(parts[1]);
                    }
                });
                
                params.filter_99_werk_codes = Array.from(werkCodes);
                params.filter_99_subwerk_codes = Array.from(subwerkCodes);
            }
        }

        if (jaarversie) {
            params.versie_jaarversie = jaarversie;
        }

        if (release) {
            params.versie = release;
        }

        if (sortConfig) {
            params.sort_by = sortConfig.key;
            params.sort_direction = sortConfig.direction;
        }

        return params;
    }, [debouncedBeschrijving, categoryIds, subcategoryIds, sortConfig, selectedHoofdcodes, jaarversie, release]);

    const download = useCallback((format: 'csv' | 'xlsx'): void => {
        loader.show("Export voorbereiden...");

        downloadFromAPI(
            {
                method: 'post',
                path: '/hoofdcode/export',
                data: {
                    search_params: getCurrentFilterParams(),
                    format: format
                }
            },
            `hoofdcodes_export.${format}`,
            {
                finally: loader.hide
            }
        );
    }, [getCurrentFilterParams, loader, downloadFromAPI]);

    const handlePageChange = useCallback((newPage: number) => {
        const totalPages = Math.ceil(totalCount / pageSize);
        const safePage = Math.min(Math.max(1, newPage), Math.max(1, totalPages));
        
        dispatch(hoofdcodeFilterActions.set_active_page(safePage));
    }, [totalCount, pageSize, dispatch]);

    const totalPages = useMemo(() => Math.ceil(totalCount / pageSize), [totalCount, pageSize]);
    
    const handleGoToPage = useCallback(() => {
        const pageNum = parseInt(pageInput);
        if (pageNum >= 1 && pageNum <= totalPages) {
            handlePageChange(pageNum);
            setPageInput("");
        }
    }, [pageInput, totalPages, handlePageChange]);

    const handleKeyPress = useCallback((e: React.KeyboardEvent) => {
        if (e.key === 'Enter') {
            handleGoToPage();
        }
    }, [handleGoToPage]);

    const validActivePage = useMemo(() => {
        if (totalCount <= 0) return 1;
        const totalPages = Math.ceil(totalCount / pageSize);
        return Math.min(Math.max(1, activePage), totalPages);
    }, [totalCount, pageSize, activePage]);

    useEffect(() => {
        apiService.get<string[]>("/hoofdcode/jaarversies").then((res) => {
            setBeschikbareJaarversies(res.data);
        });
    }, []);

    // NIEUW: "release versie" opties, cascaderend onder jaarversie — hergebruikt hetzelfde
    // endpoint als de bestekken-filter, want release is een bestek-eigenschap.
    useEffect(() => {
        const params = jaarversie ? `?versie_jaarversie=${encodeURIComponent(jaarversie)}` : '';
        apiService.get<string[]>(`/bestek/releases${params}`).then((res) => {
            setBeschikbareReleases(res.data);
        });
    }, [jaarversie]);

    useEffect(() => {
        fetch();
    }, [fetch]);

    useEffect(() => {
        if (totalCount > 0 && hoofdcodes !== undefined && !loading) {
            const totalPages = Math.ceil(totalCount / pageSize);
            if (activePage > totalPages) {
                dispatch(hoofdcodeFilterActions.set_active_page(totalPages));
            }
        }
    }, [totalCount, activePage, pageSize, hoofdcodes, loading, dispatch]);

    const tableRows = useMemo(() => {
        if (loading) {
            return (
                <tr>
                    <td colSpan={5} align="center">
                        <Spinner size="sm" className="me-2" />
                        Laden...
                    </td>
                </tr>
            );
        }
    
        if (totalCount === 0) {
            return (
                <tr>
                    <td colSpan={5} align="center">
                        Er zijn geen hoofdcodes die voldoen aan de zoekcriteria
                    </td>
                </tr>
            );
        }
    
        if (!hoofdcodes || hoofdcodes.length === 0) {
            if (totalCount > 0) {
                return (
                    <tr>
                        <td colSpan={5} align="center">
                            Geen resultaten op deze pagina. Navigeer naar een andere pagina.
                        </td>
                    </tr>
                );
            } else {
                return (
                    <tr>
                        <td colSpan={5} align="center">
                            Geen data beschikbaar
                        </td>
                    </tr>
                );
            }
        }
    
        return hoofdcodes.map(hc => {
            return (
                <tr 
                    key={`hoofdcode-id-${hc.id}`} 
                    onClick={() => navigate(`/hoofdcodes/${hc.id}`)}
                    style={{ cursor: 'pointer' }}
                >
                    <td>{hc.label}</td>
                    <td>{hc.versie_jaarversie}</td>
                    <td>{hc.omschrijving}</td>
                    <td>{hc.hoofdcode_usage?.vermeldingen || 0}</td>
                    <td>{hc.hoofdcode_usage?.bestekken || 0}</td>
                </tr>
            );
        });
    }, [totalCount, hoofdcodes, navigate, loading]);

    return (
        <div className="pt-4">
            <div className="d-flex justify-content-between align-items-center mb-3">
                <h5 className="mb-0 d-flex align-items-center gap-1">
                    Zoekfilters
                    <FilterInfoButton
                        id="hoofdcodes-filter-uitleg"
                        text="Zoekt op codenummer of omschrijving. De categorie-filters werken als trechter: elke keuze verkleint de opties in de volgende filter. Vermeldingen = hoe vaak een hoofdcode in bestekken is opgenomen. Aantal bestekken = in hoeveel unieke bestekken. Deze aantallen veranderen niet met uw zoekterm."
                    />
                </h5>
                <div className="d-flex align-items-center gap-2">
                    {totalCount >= 0 && (
                        <span className="text-muted" style={{ fontSize: '13px' }}>
                            {totalCount} {totalCount === 1 ? 'resultaat' : 'resultaten'}
                        </span>
                    )}
                    <Dropdown>
                        <Dropdown.Toggle variant="outline-secondary" size="sm">
                            <MdDownload className="me-1" />Export
                        </Dropdown.Toggle>
                        <Dropdown.Menu align="end">
                            <Dropdown.Item onClick={() => setCsvExportModalShown(true)}>
                                Aangepaste export
                            </Dropdown.Item>
                            <Dropdown.Divider />
                            <Dropdown.ItemText className="small text-muted">Snelle export (alle kolommen):</Dropdown.ItemText>
                            {totalCount > 0 && totalCount <= 1000 ? (
                                <>
                                    <Dropdown.Item onClick={() => download('csv')}>CSV</Dropdown.Item>
                                    <Dropdown.Item onClick={() => download('xlsx')}>Excel</Dropdown.Item>
                                </>
                            ) : totalCount > 1000 ? (
                                <Dropdown.ItemText className="small text-muted">
                                    Gebruik aangepaste export voor &gt;1000 resultaten.
                                </Dropdown.ItemText>
                            ) : (
                                <Dropdown.ItemText className="small text-muted">Geen resultaten.</Dropdown.ItemText>
                            )}
                        </Dropdown.Menu>
                    </Dropdown>
                    <Button
                        variant="outline-secondary"
                        size="sm"
                        onClick={() => dispatch(hoofdcodeFilterActions.toggle_filter_visibility())}
                    >
                        {filterVisible ? 'Filter verbergen' : 'Filter tonen'}
                    </Button>
                </div>
            </div>

            <div>

                {filterVisible && (
                    <Container fluid>
                        <Row className="g-3">
                            <Col xs="auto">
                                <label className="form-label mb-1">Jaarversie</label>
                                <Form.Select
                                    value={jaarversie ?? ""}
                                    onChange={(e) => dispatch(hoofdcodeFilterActions.set_jaarversie(e.target.value || null))}
                                    style={{ width: '130px' }}
                                >
                                    <option value="">Alle versies</option>
                                    {beschikbareJaarversies.map((v) => (
                                        <option key={v} value={v}>{v}</option>
                                    ))}
                                </Form.Select>
                            </Col>

                            <Col xs="auto">
                                <label className="form-label mb-1">Release versie</label>
                                <Form.Select
                                    value={release ?? ""}
                                    onChange={(e) => dispatch(hoofdcodeFilterActions.set_release(e.target.value || null))}
                                    style={{ width: '170px' }}
                                >
                                    <option value="">Alle releases</option>
                                    {beschikbareReleases.map((v) => (
                                        <option key={v} value={v}>{v}</option>
                                    ))}
                                </Form.Select>
                            </Col>

                            <Col md={6} lg={3}>
                                <label className="form-label mb-1">Omschrijving</label>
                                <Form.Control
                                    value={beschrijving ?? ""}
                                    type="text"
                                    placeholder="Zoek op omschrijving..."
                                    onChange={(e: any) => dispatch(hoofdcodeFilterActions.set_beschrijving(e.target.value))}
                                />
                                {beschrijving !== debouncedBeschrijving && (
                                    <small className="text-muted">Zoeken...</small>
                                )}
                            </Col>

                            <Col lg={2} md={4} sm={12}>
                                <HoofdcodeWerkCategorieFilter
                                    onChange={(v) => dispatch(hoofdcodeFilterActions.set_werk_categorieen(v))}
                                    selected={categories}
                                />
                            </Col>

                            <Col lg={2} md={4} sm={12}>
                                <HoofdcodeSubwerkCategorieFilter
                                    onChange={(v) => dispatch(hoofdcodeFilterActions.set_subwerk_categorieen(v))}
                                    selected={subcategories}
                                    werkcats={categories}
                                />
                            </Col>

                            <Col lg={3} md={4} sm={12}>
                                <HoofdCodeFilter
                                    werkcats={categories}
                                    subwerkcats={subcategories}
                                    selected={selectedHoofdcodes}
                                    onChange={(v) => dispatch(hoofdcodeFilterActions.set_hoofdcodes(v))}
                                    usage="hoofdcode_list"
                                    loading={loading}
                                />
                            </Col>
                        </Row>
                    </Container>
                )}
            </div>

            {loading && (
                <div className="mt-4 text-center">
                    <Spinner animation="border" role="status" size="sm">
                        <span className="visually-hidden">Loading...</span>
                    </Spinner>
                    <span className="ms-2">Data ophalen...</span>
                </div>
            )}
            
            {/* Table */}
            {!loading && (
                <>
                    <Table className="mt-3" size="sm" striped hover>
                        <thead>
                            <tr>
                                <SortableHeader
                                    sortKey="label"
                                    currentSort={sortConfig}
                                    onSort={handleSort}
                                    style={{ width: '15%', minWidth: '100px' }}
                                >
                                    Label
                                </SortableHeader>
                                
                                <SortableHeader
                                    sortKey="versie_jaarversie"
                                    currentSort={sortConfig}
                                    onSort={handleSort}
                                    style={{ width: '15%', minWidth: '100px' }}
                                >
                                    Jaarversie
                                </SortableHeader>
                                
                                <SortableHeader
                                    sortKey="romptekst"
                                    currentSort={sortConfig}
                                    onSort={handleSort}
                                >
                                    Omschrijving
                                </SortableHeader>
                                
                                <SortableHeader
                                    sortKey="vermeldingen"
                                    currentSort={sortConfig}
                                    onSort={handleSort}
                                >
                                    Vermeldingen
                                </SortableHeader>
                                
                                <SortableHeader
                                    sortKey="bestekken"
                                    currentSort={sortConfig}
                                    onSort={handleSort}
                                >
                                    Aantal bestekken
                                </SortableHeader>
                            </tr>
                        </thead>
                        <tbody>
                            {tableRows}
                        </tbody>
                    </Table>

                    {/* Pagination */}
                    {totalCount > 0 && (
                        <div className="d-flex flex-wrap align-items-center justify-content-between gap-3 mt-3">
                            <div style={{ flex: 1, minWidth: '300px' }}>
                                <PaginationControl
                                    page={validActivePage}
                                    between={4}
                                    total={totalCount}
                                    limit={pageSize}
                                    changePage={handlePageChange}
                                    ellipsis={1}
                                />
                            </div>
                            
                            <div className="d-flex flex-column align-items-end">
                                <InputGroup size="sm" style={{ width: '120px' }}>
                                    <Form.Control
                                        type="number"
                                        placeholder="Pagina"
                                        value={pageInput}
                                        onChange={(e) => setPageInput(e.target.value)}
                                        onKeyPress={handleKeyPress}
                                        min={1}
                                        max={totalPages}
                                    />
                                    <Button
                                        variant="outline-secondary"
                                        onClick={handleGoToPage}
                                        disabled={!pageInput}
                                    >
                                        Ga
                                    </Button>
                                </InputGroup>
                                <small className="text-muted mt-1">
                                    Pagina {validActivePage} van {totalPages}
                                </small>
                            </div>
                        </div>
                    )}
                </>
            )}

            {/* Export Modal */}
            <HoofdcodeCSVExportModal
                show={csvExportModalShown}
                onHide={() => setCsvExportModalShown(false)}
                filterParams={getCurrentFilterParams()}
                totalCount={totalCount}
            />
        </div>
    );
};

export default connect(mapStateToProps)(HoofdcodesList);